import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Trophy } from "lucide-react";
import { formatMoney, formatNumber } from "../../lib/api";
import { Card, pctES } from "./Charts";
import { ProductosTabla, ResumenParado } from "./Tablas";

const CA = "#0f172a", CB = "#0ea5e9";
const rot = (v) => (v == null ? "—" : `${String(v).replace(".", ",")}×`);
const FILAS = [
  ["Ventas", (d) => d.kpis.ventas, formatMoney, true],
  ["Unidades vendidas", (d) => d.kpis.uds, formatNumber, true],
  ["Cuota de tus ventas", (d) => d.kpis.cuota_ventas, pctES, true],
  ["Stock (PVP)", (d) => d.kpis.stock_pvp, formatMoney, null],
  ["Stock (coste)", (d) => d.kpis.stock_pmc, formatMoney, null],
  ["Rotación", (d) => d.kpis.rotacion, rot, true],
  ["Margen potencial", (d) => d.kpis.margen_pct, pctES, true],
  ["Productos", (d) => d.kpis.productos, formatNumber, null],
  ["Stock parado (D)", (d) => d.parado.eur, formatMoney, false],
  ["Stock D real (>1 año)", (d) => d.d_real.eur, formatMoney, false],
  ["Stock con más de 1 año", (d) => d.mas_1y.eur, formatMoney, false],
  ["Caduca en 90 días", (d) => d.caducar.eur, formatMoney, false],
];

function Gana({ on }) {
  return on ? <Trophy size={12} className="inline ml-1 text-amber-500" data-testid="comparar-gana" /> : null;
}

function TablaDiferencias({ a, b }) {
  return (
    <Card title="Diferencias" sub="El trofeo marca quién sale mejor en cada indicador." testid="comparar-diferencias">
      <div className="overflow-x-auto">
        <table className="dense" data-testid="comparar-diferencias-table">
          <thead><tr><th>Indicador</th><th className="num">{a.laboratorio}</th><th className="num">{b.laboratorio}</th><th className="num">Diferencia</th></tr></thead>
          <tbody>
            {FILAS.map(([label, get, fmt, mejorAlto]) => {
              const va = get(a) ?? 0, vb = get(b) ?? 0;
              const win = mejorAlto == null || va === vb ? null : (va > vb) === mejorAlto ? "a" : "b";
              return (
                <tr key={label} data-testid={`comparar-fila-${label}`}>
                  <td className="font-semibold text-xs">{label}</td>
                  <td className="num">{fmt(get(a))}<Gana on={win === "a"} /></td>
                  <td className="num">{fmt(get(b))}<Gana on={win === "b"} /></td>
                  <td className={"num text-xs " + (win === "a" ? "text-slate-900" : win === "b" ? "text-sky-700" : "text-slate-500")}>
                    {fmt === pctES ? `${String(Math.round((va - vb) * 10) / 10).replace(".", ",")} pp` : fmt === rot ? fmt(Math.round((va - vb) * 100) / 100) : fmt(va - vb)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AbcdDoble({ a, b, k, title }) {
  const rows = a.abcd.map((r, i) => ({ code: r.code, A: r[k], B: b.abcd[i]?.[k] || 0 }));
  return (
    <Card title={title} testid={`comparar-abcd-${k}`}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} barGap={3}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} %`} />
          <Tooltip formatter={(v) => pctES(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="A" name={a.laboratorio} fill={CA} radius={[4, 4, 0, 0]} />
          <Bar dataKey="B" name={b.laboratorio} fill={CB} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

function Columna({ d, color, idx }) {
  const lab = d.laboratorio;
  return (
    <div className="space-y-6 min-w-0" data-testid={`comparar-columna-${idx}`}>
      <h3 className="font-display text-xl font-extrabold border-l-4 pl-3" style={{ borderColor: color }}>{lab}</h3>
      <ProductosTabla title="Top productos más vendidos" items={d.top} lab={lab} testid={`comparar-top-${idx}`} filename="comparar-top"
                      cols={["descripcion", "abcd", "uds_vendidas", "tot_venta"]} />
      <ProductosTabla title="Stock parado (clase D)" items={d.parado.items} lab={lab} testid={`comparar-parado-${idx}`} filename="comparar-stock-parado"
                      hover resumen={<ResumenParado d={d.parado} />} cols={["descripcion", "stock", "valor_pvp", "u_entrada"]} />
    </div>
  );
}

export function Comparacion({ a, b }) {
  return (
    <div className="space-y-6" data-testid="comparar-laboratorios">
      <TablaDiferencias a={a} b={b} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AbcdDoble a={a} b={b} k="pct_ventas" title="% de ventas por clase ABCD" />
        <AbcdDoble a={a} b={b} k="pct_stock" title="% de stock por clase ABCD" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Columna d={a} color={CA} idx="a" />
        <Columna d={b} color={CB} idx="b" />
      </div>
    </div>
  );
}
